import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Info, Loader2, X, Trash2, SlidersHorizontal, Star, ThumbsUp, Globe2 } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import {
  ADD_NEW_VALUE,
  useAdvancedFilters,
  useBusinessCategories,
  useBusinessTypes,
  useRadius,
} from "../hooks/useSearchBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchBar({ onSearch, loading, presetBusinessType = "", presetLocation = "" }) {
  const { t, lang } = useLang();
  const [businessType, setBusinessType] = useState(presetBusinessType);
  const [location, setLocation] = useState(presetLocation);
  const { unit, radius, setRadius, switchUnit, radiusOptions } = useRadius("20km");
  const [openOnly, setOpenOnly] = useState(false);
  const { types, builtin, custom, savingType, reloadTypes: _reload, saveNewType, deleteCustomType } = useBusinessTypes(t);
  const [adding, setAdding] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const adv = useAdvancedFilters();
  const { ratingMin, setRatingMin, reviewsMin, setReviewsMin, scoreMax, setScoreMax, hasWebsite, setHasWebsite, reset: resetAdvanced, advActive, advCount } = adv;
  const { categories, categoryId, setCategoryId, matchesCategory } = useBusinessCategories();
  const newInputRef = useRef(null);

  // Filter types by selected category
  const builtinFiltered = builtin.filter(matchesCategory).slice().sort((a, b) => a.localeCompare(b));
  const customFiltered = custom.filter(matchesCategory).slice().sort((a, b) => a.localeCompare(b));
  const typesFiltered = types.filter(matchesCategory).slice().sort((a, b) => a.localeCompare(b));

  // When category changes, if the currently selected business_type doesn't
  // match, clear it so the user picks a fresh one.
  useEffect(() => {
    if (categoryId && businessType && !matchesCategory(businessType)) {
      setBusinessType("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  useEffect(() => {
    if (adding) {
      const id = window.setTimeout(() => newInputRef.current?.focus(), 50);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [adding]);

  useEffect(() => { if (presetBusinessType) setBusinessType(presetBusinessType); }, [presetBusinessType]);
  useEffect(() => { if (presetLocation) setLocation(presetLocation); }, [presetLocation]);

  const submit = (e) => {
    e?.preventDefault?.();
    if (!businessType || businessType === ADD_NEW_VALUE) return;
    onSearch({
      business_type: businessType,
      location,
      radius,
      status: openOnly ? "Open" : "",
      rating_min: ratingMin || 0,
      reviews_min: reviewsMin || 0,
      score_max: scoreMax || 100,
      has_website: hasWebsite || "",
    });
  };

  const onTypeChange = (val) => {
    if (val === ADD_NEW_VALUE) {
      setAdding(true);
      setNewTypeName("");
    } else {
      setBusinessType(val);
    }
  };

  const cancelAdd = () => { setAdding(false); setNewTypeName(""); };

  const submitNewType = async (e) => {
    e?.preventDefault?.();
    const saved = await saveNewType(newTypeName, (name) => setBusinessType(name));
    if (saved) cancelAdd();
  };

  const handleDeleteCustom = (name) => {
    deleteCustomType(name, (deleted) => {
      if (businessType === deleted) setBusinessType("");
    });
  };

  return (
    <div className="space-y-2">
      {/* Category chip-row: Supplier / GC / Residencial / Comercial */}
      {categories.length > 0 && (
        <div
          data-testid="search-category-bar"
          className="flex items-center flex-wrap gap-1.5 px-1"
        >
          <span className="text-[10.5px] uppercase tracking-wider text-[#6B7280] font-bold mr-1">
            {t("search.category_label") || "Categoria"}:
          </span>
          <button
            type="button"
            data-testid="search-category-all"
            onClick={() => setCategoryId("")}
            className={
              "px-2.5 h-7 rounded-full text-[11.5px] font-semibold border transition-colors " +
              (categoryId === ""
                ? "bg-[#111827] text-white border-[#111827]"
                : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#3B82F6]")
            }
          >
            {t("search.category_all") || "Todas"}
          </button>
          {categories.map((c) => {
            const active = categoryId === c.id;
            const label = lang === "pt" ? (c.label_pt || c.label_en) : (c.label_en || c.label_pt);
            return (
              <button
                key={c.id}
                type="button"
                data-testid={`search-category-${c.id}`}
                onClick={() => setCategoryId(active ? "" : c.id)}
                style={active ? { background: c.color, borderColor: c.color, color: "#fff" } : { borderColor: `${c.color}55`, color: c.color }}
                className="px-2.5 h-7 rounded-full text-[11.5px] font-semibold border bg-white hover:opacity-90 transition-colors"
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      <form
        onSubmit={submit}
        data-testid="search-form"
        className="bg-white border border-[#E5E7EB] rounded-xl p-3 flex flex-wrap items-center gap-2 shadow-sm"
      >
        <div className="w-full sm:flex-1" style={{ flexBasis: undefined }}>
          {!adding ? (
            <div className="flex items-center gap-1.5">
              <select
                data-testid="search-business-type-select"
                value={businessType}
                onChange={(e) => onTypeChange(e.target.value)}
                className="flex-1 h-11 px-3.5 rounded-lg border border-[#E5E7EB] focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/15 text-[14px] bg-white"
              >
                <option value="">{t("search.business_placeholder")}</option>
                {builtinFiltered.length > 0 && (
                  <optgroup label={t("search.types_builtin")}>
                    {builtinFiltered.map((tp) => (<option key={tp} value={tp}>{tp}</option>))}
                  </optgroup>
                )}
                {customFiltered.length > 0 && (
                  <optgroup label={t("search.types_custom")}>
                    {customFiltered.map((tp) => (<option key={tp} value={tp}>{tp}</option>))}
                  </optgroup>
                )}
                {/* Fallback when builtin/custom split not available yet */}
                {builtin.length === 0 && typesFiltered.map((tp) => (<option key={tp} value={tp}>{tp}</option>))}
                <option value={ADD_NEW_VALUE}>{t("search.types_add_option")}</option>
              </select>
              <button
                type="button"
                data-testid="search-new-type-trigger"
                onClick={() => { setAdding(true); setNewTypeName(""); }}
                title={t("search.types_add_option")}
                aria-expanded={adding}
                className="shrink-0 h-11 w-11 rounded-lg border border-[#E5E7EB] bg-white hover:bg-[#EFF6FF] hover:border-[#3B82F6] hover:text-[#3B82F6] flex items-center justify-center text-[#6B7280] text-[18px] font-bold transition-colors"
              >+</button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 h-11 px-2 rounded-lg border border-[#3B82F6] bg-[#EFF6FF]">
              <input
                ref={newInputRef}
                data-testid="search-new-type-input"
                type="text"
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submitNewType(e); if (e.key === "Escape") cancelAdd(); }}
                placeholder={t("search.types_add_placeholder")}
                className="flex-1 h-9 px-2 rounded-md border border-transparent bg-white focus:outline-none focus:border-[#3B82F6] text-[13.5px]"
              />
              <button
                type="button"
                data-testid="search-new-type-save"
                onClick={submitNewType}
                disabled={savingType || newTypeName.trim().length < 2}
                className="h-9 px-3 rounded-md bg-[#3B82F6] text-white text-[12px] font-semibold hover:bg-[#2563EB] disabled:opacity-50"
              >
                {savingType ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t("search.types_save")}
              </button>
              <button
                type="button"
                data-testid="search-new-type-cancel"
                onClick={cancelAdd}
                className="h-9 w-9 rounded-md text-[#6B7280] hover:text-[#DC2626] inline-flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
        <div className="relative w-full sm:w-auto" style={{ flexBasis: undefined, flexGrow: 1 }}>
          <input
            data-testid="search-location-input"
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder={t("search.location_placeholder")}
            className="w-full h-11 pl-4 pr-9 rounded-lg border border-[#E5E7EB] focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/15 text-[14px] placeholder:text-[#9CA3AF]"
          />
          <Info className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
        </div>

        {/* Structured location dropdowns — City · State · Zipcode (Iter43) */}
        <StructuredLocation location={location} setLocation={setLocation} t={t} />


        {/* KM/MI unit toggle */}
        <div
          data-testid="radius-unit-toggle"
          className="inline-flex h-11 rounded-lg border border-[#E5E7EB] overflow-hidden bg-white text-[12px] font-semibold"
        >
          <button
            type="button"
            data-testid="unit-km-btn"
            onClick={() => switchUnit("km")}
            className={"px-3 transition-colors " + (unit === "km" ? "bg-[#3B82F6] text-white" : "text-[#6B7280] hover:bg-[#F3F4F6]")}
          >
            {t("search.unit_km")}
          </button>
          <button
            type="button"
            data-testid="unit-mi-btn"
            onClick={() => switchUnit("mi")}
            className={"px-3 transition-colors " + (unit === "mi" ? "bg-[#3B82F6] text-white" : "text-[#6B7280] hover:bg-[#F3F4F6]")}
          >
            {t("search.unit_mi")}
          </button>
        </div>

        <div style={{ minWidth: 90 }}>
          <select
            data-testid="search-radius-select"
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
            className="w-full h-11 px-3 rounded-lg border border-[#E5E7EB] focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/15 text-[14px] bg-white"
          >
            {radiusOptions.map((r) => (<option key={r} value={r}>{r}</option>))}
          </select>
        </div>

        <button
          data-testid="search-submit-btn"
          type="submit"
          disabled={loading}
          className="al-btn-primary h-11 px-5 rounded-lg flex items-center justify-center gap-2 font-medium disabled:opacity-60"
          style={{ minWidth: 110 }}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {t("search.button")}
        </button>
      </form>

      {/* Status filter chips */}
      <div className="flex items-center gap-2 px-1">
        <span className="text-[12px] text-[#6B7280] font-medium">{t("search.status_label")}</span>
        <button
          type="button"
          data-testid="status-all-chip"
          onClick={() => setOpenOnly(false)}
          className={
            "px-3 h-7 rounded-full text-[11.5px] font-medium border transition-all " +
            (!openOnly
              ? "bg-[#1A1A2E] text-white border-[#1A1A2E]"
              : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#3B82F6] hover:text-[#3B82F6]")
          }
        >
          {t("search.status_all")}
        </button>
        <button
          type="button"
          data-testid="status-open-chip"
          onClick={() => setOpenOnly(true)}
          className={
            "px-3 h-7 rounded-full text-[11.5px] font-medium border transition-all " +
            (openOnly
              ? "bg-[#22C55E] text-white border-[#22C55E]"
              : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#3B82F6] hover:text-[#3B82F6]")
          }
        >
          {t("search.status_open")}
        </button>

        {/* Advanced filters toggle */}
        <button
          type="button"
          data-testid="search-advanced-toggle"
          onClick={() => setShowAdvanced((v) => !v)}
          className={
            "ml-2 inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[11.5px] font-semibold border transition-all " +
            (showAdvanced || advActive
              ? "bg-[#3B82F6] text-white border-[#3B82F6]"
              : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#3B82F6] hover:text-[#3B82F6]")
          }
        >
          <SlidersHorizontal className="w-3 h-3" />
          {t("search.advanced")}
          {advCount > 0 && (
            <span className="ml-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white text-[#3B82F6] text-[9.5px] font-bold">
              {advCount}
            </span>
          )}
        </button>

        {/* Custom types row (shown only when there are custom types) */}
        {custom.length > 0 && (
          <div className="ml-auto flex items-center gap-1.5" data-testid="custom-types-row">
            <span className="text-[10.5px] text-[#9CA3AF] uppercase tracking-wider font-semibold">
              {t("search.types_custom")}:
            </span>
            {custom.slice(0, 6).map((tp) => (
              <span
                key={tp}
                data-testid={`custom-type-chip-${tp}`}
                className={
                  "inline-flex items-center gap-1 h-7 pl-2 pr-1 rounded-full border text-[11px] font-medium transition-colors " +
                  (businessType === tp
                    ? "bg-[#3B82F6] text-white border-[#3B82F6]"
                    : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#3B82F6] hover:text-[#3B82F6]")
                }
              >
                <button
                  type="button"
                  onClick={() => setBusinessType(tp)}
                  data-testid={`custom-type-select-${tp}`}
                  className="truncate max-w-[140px]"
                >
                  {tp}
                </button>
                <button
                  type="button"
                  data-testid={`custom-type-delete-${tp}`}
                  onClick={() => handleDeleteCustom(tp)}
                  title={t("search.types_delete")}
                  className={
                    "w-5 h-5 rounded-full inline-flex items-center justify-center " +
                    (businessType === tp ? "hover:bg-white/20" : "hover:bg-[#FEE2E2] hover:text-[#DC2626]")
                  }
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Advanced filters panel */}
      {showAdvanced && (
        <div
          data-testid="advanced-filters-panel"
          className="bg-white border border-[#E5E7EB] rounded-xl p-3 shadow-sm grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
        >
          {/* Rating min */}
          <FilterField label={t("search.adv_rating_min")} icon={Star} testid="adv-rating-min">
            <select
              data-testid="adv-rating-select"
              value={ratingMin}
              onChange={(e) => setRatingMin(Number(e.target.value))}
              className="w-full h-9 px-2.5 rounded-lg border border-[#E5E7EB] focus:outline-none focus:border-[#3B82F6] text-[12.5px] bg-white"
            >
              <option value={0}>{t("search.adv_any")}</option>
              <option value={3}>3.0 ★+</option>
              <option value={3.5}>3.5 ★+</option>
              <option value={4}>4.0 ★+</option>
              <option value={4.5}>4.5 ★+</option>
              <option value={5}>5.0 ★</option>
            </select>
          </FilterField>

          {/* Reviews min */}
          <FilterField label={t("search.adv_reviews_min")} icon={ThumbsUp} testid="adv-reviews-min">
            <select
              data-testid="adv-reviews-select"
              value={reviewsMin}
              onChange={(e) => setReviewsMin(Number(e.target.value))}
              className="w-full h-9 px-2.5 rounded-lg border border-[#E5E7EB] focus:outline-none focus:border-[#3B82F6] text-[12.5px] bg-white"
            >
              <option value={0}>{t("search.adv_any")}</option>
              <option value={5}>5+</option>
              <option value={20}>20+</option>
              <option value={50}>50+</option>
              <option value={100}>100+</option>
              <option value={500}>500+</option>
            </select>
          </FilterField>

          {/* Score max */}
          <FilterField label={t("search.adv_score_max")} icon={Search} testid="adv-score-max">
            <select
              data-testid="adv-score-select"
              value={scoreMax}
              onChange={(e) => setScoreMax(Number(e.target.value))}
              className="w-full h-9 px-2.5 rounded-lg border border-[#E5E7EB] focus:outline-none focus:border-[#3B82F6] text-[12.5px] bg-white"
            >
              <option value={100}>{t("search.adv_any")}</option>
              <option value={33}>{t("search.adv_only_quente")}</option>
              <option value={66}>{t("search.adv_quente_morno")}</option>
              <option value={50}>≤ 50</option>
              <option value={75}>≤ 75</option>
            </select>
          </FilterField>

          {/* Has website */}
          <FilterField label={t("search.adv_has_website")} icon={Globe2} testid="adv-has-website">
            <div className="inline-flex h-9 rounded-lg border border-[#E5E7EB] overflow-hidden bg-white text-[12px] font-semibold w-full">
              {[
                { v: "",    label: t("search.adv_any") },
                { v: "yes", label: t("search.adv_yes") },
                { v: "no",  label: t("search.adv_no") },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  data-testid={`adv-website-${opt.v || "any"}`}
                  onClick={() => setHasWebsite(opt.v)}
                  className={
                    "flex-1 transition-colors " +
                    (hasWebsite === opt.v ? "bg-[#3B82F6] text-white" : "text-[#6B7280] hover:bg-[#F3F4F6]")
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </FilterField>

          {advActive && (
            <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
              <button
                type="button"
                data-testid="adv-reset"
                onClick={resetAdvanced}
                className="text-[11.5px] text-[#6B7280] hover:text-[#DC2626] underline"
              >
                {t("search.adv_reset")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FilterField({ label, icon: Icon, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="flex items-center gap-1 text-[10.5px] uppercase tracking-wider font-bold text-[#6B7280] mb-1">
        <Icon className="w-3 h-3" /> {label}
      </span>
      {children}
    </label>
  );
}

/**
 * StructuredLocation — 3 dropdowns (State · City · Zipcode) synced with the
 * free-text `location` field. Lets users pick exactly "Miami, FL 33101"
 * without typing, while preserving the free-text fallback.
 */
function StructuredLocation({ location, setLocation, t }) {
  const [open, setOpen] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [zips, setZips] = useState([]);
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");

  useEffect(() => {
    axios.get(`${API}/locations/states`).then(({ data }) => setStates(data.items || [])).catch(() => setStates([]));
  }, []);

  useEffect(() => {
    const params = { limit: 80 };
    if (state) params.state = state;
    // Iter331 · Unify with shared taxonomy — merge Prospecting's
    // /locations/cities (rooted in leads collection) with /taxonomy/cities
    // (the single source of truth used by Leads Finder).
    Promise.all([
      axios.get(`${API}/locations/cities`, { params }).catch(() => ({ data: {} })),
      axios.get(`${API}/taxonomy`).catch(() => ({ data: {} })),
    ]).then(([loc, tax]) => {
      const merged = new Map();
      (loc.data.items || []).forEach((c) => merged.set(c.city, {
        city: c.city, leads: c.leads || 0, source: "leads",
      }));
      (tax.data.cities || []).forEach((c) => {
        const key = c.label;
        if (state && c.state && c.state !== state) return;
        const prev = merged.get(key);
        merged.set(key, {
          city: key,
          leads: (prev && prev.leads) || c.count || 0,
          source: prev ? "both" : "taxonomy",
        });
      });
      setCities([...merged.values()].sort((a, b) => a.city.localeCompare(b.city)));
    });
  }, [state]);

  useEffect(() => {
    const params = { limit: 60 };
    if (state) params.state = state;
    if (city) params.city = city;
    axios.get(`${API}/locations/zipcodes`, { params })
      .then(({ data }) => setZips(data.items || []))
      .catch(() => setZips([]));
  }, [state, city]);

  const pushUp = (c, s, z) => {
    const parts = [];
    if (c) parts.push(c);
    if (s) parts.push(s);
    // Iter108 — always append "USA" when only a US state is selected (no
    // city). Otherwise Google's Geocoding API resolves bare "Georgia" to
    // the country 🇬🇪 and the search returns no results / wrong leads.
    if (s && !c) parts.push("USA");
    const base = parts.join(", ");
    setLocation([base, z].filter(Boolean).join(" ").trim());
  };

  return (
    <div className="relative w-full" style={{ flexBasis: "100%" }}>
      <button
        type="button"
        data-testid="search-structured-toggle"
        onClick={() => setOpen((o) => !o)}
        className="h-9 px-3 mt-1 rounded-md border border-[#E5E7EB] bg-white text-[11.5px] font-semibold text-[#6B7280] hover:border-[#3B82F6] hover:text-[#3B82F6] inline-flex items-center gap-1.5"
      >
        <Globe2 className="w-3.5 h-3.5" />
        {open ? t("search.structured_hide") : t("search.structured_show")}
      </button>
      {open && (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="search-structured-row">
          <select
            data-testid="search-state-select"
            value={state}
            onChange={(e) => { const v = e.target.value; setState(v); setCity(""); setZip(""); pushUp("", v, ""); }}
            className="h-10 px-3 rounded-md border border-[#E5E7EB] bg-white text-[13px] focus:outline-none focus:border-[#3B82F6]"
          >
            <option value="">{t("search.state_all")}</option>
            {states.map((s) => <option key={s.code} value={s.code}>{s.code} · {s.name}</option>)}
          </select>

          {/* City — combobox: dropdown + free-text fallback. */}
          <div className="relative">
            <input
              data-testid="search-city-select"
              list="city-options-list"
              value={city}
              onChange={(e) => { const v = e.target.value; setCity(v); setZip(""); pushUp(v, state, ""); }}
              placeholder={t("search.city_all")}
              className="w-full h-10 px-3 rounded-md border border-[#E5E7EB] bg-white text-[13px] focus:outline-none focus:border-[#3B82F6]"
            />
            <datalist id="city-options-list">
              {cities.map((c) => <option key={c.city} value={c.city}>{c.leads > 0 ? `${c.leads} salvos` : ""}</option>)}
            </datalist>
            {cities.length === 0 && state && (
              <span className="absolute -bottom-4 left-1 text-[10px] text-[#9CA3AF]">
                {t("search.city_empty") || "Digite manualmente"}
              </span>
            )}
          </div>

          {/* ZIP — combobox: dropdown of ZIPs for the selected city + free-text fallback */}
          <div className="relative">
            <input
              data-testid="search-zip-select"
              list="zip-options-list"
              value={zip}
              onChange={(e) => { const v = e.target.value; setZip(v); pushUp(city, state, v); }}
              placeholder={t("search.zip_all")}
              className="w-full h-10 px-3 rounded-md border border-[#E5E7EB] bg-white text-[13px] focus:outline-none focus:border-[#3B82F6]"
            />
            <datalist id="zip-options-list">
              {zips.map((z) => <option key={z.zip} value={z.zip}>{z.leads > 0 ? `${z.leads} salvos` : ""}</option>)}
            </datalist>
          </div>
        </div>
      )}
    </div>
  );
}

